// Run-splitting for vehicle bands (REDESIGN v2: connected tour bands).
//
// A "band" is one vehicle's continuous commitment: either a single standalone
// show, or a whole TOUR rendered as ONE connected band — leave → drive → show →
// drive-between → show → … → drive → home. The band's leave/return come from the
// tour-bookend rule (first event's ship_out, last event's ship_back); interior
// gaps between consecutive shows render as drive-between blocks so the tour reads
// as one continuous run rather than separate islands.

import { shiftDay } from '../utils/date-utils';
import { VehicleScheduleEvent, VehicleAssignment } from './types';

export type RunKind = 'leave' | 'drive_out' | 'show' | 'drive_between' | 'drive_back' | 'home';

export interface BandRun {
  kind: RunKind;
  start: string; // inclusive YYYY-MM-DD
  end: string; // inclusive YYYY-MM-DD
  label: string;
  /** Leg runs (everything but the show) render as faded transit blocks. */
  leg: boolean;
  /** Which assignment this run belongs to — for click-to-edit / unassign. */
  assignmentId: string;
  eventId: string;
}

/** The band's extent for a SINGLE event = its ship window, fallback to show span. */
export function bandExtent(event: VehicleScheduleEvent): { start: string; end: string } {
  const start = event.ship_out_date || event.event_date;
  const end = event.ship_back_date || event.end_date || event.event_date;
  return { start, end: end < start ? start : end };
}

/**
 * The "stacked" cluster around a target show on one vehicle: the target plus every
 * other show in `pool` whose ship window OVERLAPS it — i.e. the shows that share
 * stacked days with it. Returned sorted by ship-out, ready to group into a tour;
 * empty if the target stacks with nothing (cluster < 2). Same-vehicle stacking is
 * overlapping travel windows for sequential shows — the normal tour shape.
 */
export function stackedWith(target: VehicleScheduleEvent, pool: VehicleScheduleEvent[]): VehicleScheduleEvent[] {
  const t = bandExtent(target);
  const cluster = pool.filter(e => {
    const x = bandExtent(e);
    return x.start <= t.end && t.start <= x.end; // overlaps the target (includes the target)
  });
  if (cluster.length < 2) return [];
  return cluster.sort((a, b) => {
    const sa = bandExtent(a).start, sb = bandExtent(b).start;
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  });
}

/**
 * The note for one transit leg, from the RIGHT family of the two that exist.
 *
 * `event_vehicle.drive_*_note` belongs to one truck on one show and is typed on
 * the vehicle band; `events.transport_*_note` belongs to the show's external
 * freight and is typed on the Shipping / Rentals band. A show can now carry both
 * a truck and shipped freight at once, so these must never fall through to each
 * other: a chained `a?.drive_out_note || e.transport_out_note` printed "FedEx
 * Freight, PRO 774451" on a box truck's band as that truck's driving instruction.
 *
 * The assignment IS the discriminator — a rental band is built with an empty
 * assignment map, so `a` is undefined there and only the event note applies.
 */
function legNote(
  a: VehicleAssignment | undefined,
  edge: 'out' | 'back',
  event: VehicleScheduleEvent,
): string | null {
  if (a) return (edge === 'out' ? a.drive_out_note : a.drive_back_note) || null;
  return (edge === 'out' ? event.transport_out_note : event.transport_back_note) || null;
}

/**
 * Build the run segments for a band given its events (ordered by show date), the
 * assignment serving each, and the effective leave/return (which the drag preview
 * may shift). Works for one event (standalone) or many (a tour).
 */
export function buildBandRuns(
  events: VehicleScheduleEvent[],
  assignmentByEventId: Record<string, VehicleAssignment>,
  leave: string,
  ret: string,
  // Use the shipping vocabulary ("ship out / in transit / received") instead of
  // "leave HQ / drive / home". The CALLER decides: vehicle-lane bands are always
  // driving (owned truck), so they pass false even when an event's transport_mode
  // defaults to 'shipping'; only the Shipping/Rentals lane passes true for shipped
  // bands. (Fixes owned-vehicle bands wrongly showing "ship out" from the default.)
  shipMode: boolean = false,
): BandRun[] {
  if (!events.length) return [];
  const runs: BandRun[] = [];
  const first = events[0];
  const last = events[events.length - 1];
  const firstA = assignmentByEventId[first.id];
  const lastA = assignmentByEventId[last.id];
  const firstShowStart = first.event_date;
  const lastShowEnd = last.end_date || last.event_date;

  // ── leave + drive-out (before the first show) ──
  if (leave < firstShowStart) {
    const origin = firstA?.origin_label || 'HQ';
    runs.push({ kind: 'leave', start: leave, end: leave, label: shipMode ? 'ship out' : `leave ${origin}`, leg: true, assignmentId: firstA?.id, eventId: first.id });
    const dStart = shiftDay(leave, 1);
    const dEnd = shiftDay(firstShowStart, -1);
    if (dStart <= dEnd) {
      runs.push({ kind: 'drive_out', start: dStart, end: dEnd, label: legNote(firstA, 'out', first) || (shipMode ? 'in transit' : 'drive'), leg: true, assignmentId: firstA?.id, eventId: first.id });
    }
  }

  // ── shows, with drive-between filling the gaps ──
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    const a = assignmentByEventId[e.id];
    const sStart = e.event_date;
    const sEnd = e.end_date || e.event_date;
    runs.push({ kind: 'show', start: sStart, end: sEnd, label: e.name, leg: false, assignmentId: a?.id, eventId: e.id });

    if (i < events.length - 1) {
      const next = events[i + 1];
      const nextA = assignmentByEventId[next.id];
      const gapStart = shiftDay(sEnd, 1);
      const gapEnd = shiftDay(next.event_date, -1);
      if (gapStart <= gapEnd) {
        // The transit between two shows: prefer this leg's back note, else the
        // next leg's out note, else a plain "drive". Both reads stay inside one
        // family (see legNote) — a truck's band never borrows the show's shipping
        // note, and vice versa.
        const label = legNote(a, 'back', e) || legNote(nextA, 'out', next) || (shipMode ? 'in transit' : 'drive');
        runs.push({ kind: 'drive_between', start: gapStart, end: gapEnd, label, leg: true, assignmentId: a?.id, eventId: e.id });
      }
    }
  }

  // ── drive-back + home (after the last show) ──
  if (ret > lastShowEnd) {
    const dStart = shiftDay(lastShowEnd, 1);
    const dEnd = shiftDay(ret, -1);
    if (dStart <= dEnd) {
      runs.push({ kind: 'drive_back', start: dStart, end: dEnd, label: legNote(lastA, 'back', last) || (shipMode ? 'in transit' : 'drive'), leg: true, assignmentId: lastA?.id, eventId: last.id });
    }
    runs.push({ kind: 'home', start: ret, end: ret, label: shipMode ? 'received' : 'home', leg: true, assignmentId: lastA?.id, eventId: last.id });
  }

  return runs;
}

/** A band group: a standalone show OR a whole tour, for one vehicle. The runs
 *  are built (drag-aware) in the component from baseLeave/baseReturn + events. */
export interface DerivedVehicleBand {
  key: string;
  events: VehicleScheduleEvent[]; // ordered by show date
  assignmentByEventId: Record<string, VehicleAssignment>;
  baseLeave: string;
  baseReturn: string;
  isTour: boolean;
  tourName: string | null;
  // Drag metadata: the left edge edits the first event's ship_out, the right
  // edge the last event's ship_back.
  firstEventId: string;
  firstEventDate: string;
  lastEventId: string;
  lastEventEnd: string;
  /** Vertical sub-lane within the vehicle row. >0 means it overlaps another band
   *  for this vehicle (usually a legacy-data artifact) and is stacked below it. */
  subLane: number;
  /** True when this band's window overlaps another band on the SAME vehicle — a
   *  physical conflict (one vehicle, two places). The grid flags it in a warning color. */
  conflict: boolean;
}

/**
 * Group a vehicle's assignments into bands. Tour members of the same vehicle
 * collapse into ONE band (keyed by tour); standalone events are their own band.
 * (A second vehicle covering just one tour show gets a single-event band keyed by
 * that tour — it still reads as tour-related via the outline.)
 */
export function deriveVehicleBands(
  assignments: VehicleAssignment[],
  eventById: Map<string, VehicleScheduleEvent>,
  tourNameById: Map<string, string>,
): Map<string, DerivedVehicleBand[]> {
  // vehicle_id → groupKey → { events, assignmentByEventId, tourId }
  const byVehicle = new Map<string, Map<string, { events: VehicleScheduleEvent[]; byEvent: Record<string, VehicleAssignment>; tourId: string | null }>>();

  for (const a of assignments) {
    const e = eventById.get(a.event_id);
    if (!e) continue;
    // Group key: per-(vehicle, tour) for tour members, else per-assignment.
    const groupKey = e.tour_id ? `tour:${e.tour_id}` : `solo:${a.id}`;
    let groups = byVehicle.get(a.vehicle_id);
    if (!groups) { groups = new Map(); byVehicle.set(a.vehicle_id, groups); }
    let g = groups.get(groupKey);
    if (!g) { g = { events: [], byEvent: {}, tourId: e.tour_id }; groups.set(groupKey, g); }
    g.events.push(e);
    g.byEvent[e.id] = a;
  }

  const result = new Map<string, DerivedVehicleBand[]>();
  for (const [vehicleId, groups] of byVehicle) {
    const bands: DerivedVehicleBand[] = [];
    for (const [key, g] of groups) {
      const events = [...g.events].sort((x, y) =>
        x.event_date < y.event_date ? -1 : x.event_date > y.event_date ? 1 : (x.id < y.id ? -1 : 1)
      );
      const first = events[0];
      const last = events[events.length - 1];
      const isTour = !!g.tourId;
      // Bookend rule: leave = first event's ship_out, return = last event's
      // ship_back (works for standalone too — first === last).
      const baseLeave = first.ship_out_date || first.event_date;
      const baseReturn = last.ship_back_date || last.end_date || last.event_date;
      bands.push({
        key,
        events,
        assignmentByEventId: g.byEvent,
        baseLeave,
        baseReturn,
        isTour,
        tourName: g.tourId ? (tourNameById.get(g.tourId) ?? 'Tour') : null,
        firstEventId: first.id,
        firstEventDate: first.event_date,
        lastEventId: last.id,
        lastEventEnd: last.end_date || last.event_date,
        subLane: 0,
        conflict: false,
      });
    }
    // Stack overlapping bands into sub-lanes (greedy interval packing by
    // [baseLeave..baseReturn]). A vehicle can't be two places at once, so an
    // overlap is a conflict (usually legacy tour data) — stacking keeps both
    // bands fully visible instead of letting them muddy together.
    const sorted = [...bands].sort((a, b) => (a.baseLeave < b.baseLeave ? -1 : a.baseLeave > b.baseLeave ? 1 : 0));
    const laneEnds: string[] = [];
    for (const band of sorted) {
      let lane = laneEnds.findIndex(end => end < band.baseLeave);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(band.baseReturn); }
      else laneEnds[lane] = band.baseReturn;
      band.subLane = lane;
    }
    // A vehicle can't be two places at once: flag every band whose window overlaps
    // another band on this vehicle so the grid can warn with a conflict color.
    for (const a of bands) {
      a.conflict = bands.some(b => b !== a && a.baseLeave <= b.baseReturn && b.baseLeave <= a.baseReturn);
    }
    result.set(vehicleId, bands);
  }
  return result;
}

/**
 * Shipping / Rentals bands packed into non-overlapping lanes.
 *
 * Grouping mirrors `deriveVehicleBands` on purpose: the arranged stops of ONE
 * tour become ONE band under the bookend rule (first stop's ship_out through the
 * last stop's ship_back), and a standalone show is its own band. A show can carry
 * a truck and external transport at once, so its vehicle band and its lane band
 * have to be the same shape and answer an edge drag with the same stop's dates;
 * one band per stop drew a three-stop tour as three overlapping windows stacked
 * into three lanes beside one connected truck band, and dragging the middle one
 * wrote the middle stop's date while the truck band wrote the first stop's.
 *
 * A band here has no event_vehicle row, so `assignmentByEventId` is empty and
 * `buildBandRuns` reads only the event-level transport notes (see `legNote`).
 * Overlapping bands split into separate lanes; non-overlapping ones share a row.
 * The caller always renders one extra empty "spare" lane.
 */
export function deriveRentalLanes(
  rentalEvents: VehicleScheduleEvent[],
  tourNameById: Map<string, string>,
): DerivedVehicleBand[][] {
  const groups = new Map<string, { events: VehicleScheduleEvent[]; tourId: string | null }>();
  for (const e of rentalEvents) {
    const key = e.tour_id ? `rental:tour:${e.tour_id}` : `rental:${e.id}`;
    let g = groups.get(key);
    if (!g) { g = { events: [], tourId: e.tour_id }; groups.set(key, g); }
    g.events.push(e);
  }

  const bands: DerivedVehicleBand[] = [];
  for (const [key, g] of groups) {
    const events = [...g.events].sort((x, y) =>
      x.event_date < y.event_date ? -1 : x.event_date > y.event_date ? 1 : (x.id < y.id ? -1 : 1)
    );
    const first = events[0];
    const last = events[events.length - 1];
    const baseLeave = first.ship_out_date || first.event_date;
    const rawReturn = last.ship_back_date || last.end_date || last.event_date;
    bands.push({
      key,
      events,
      assignmentByEventId: {}, // no event_vehicle row: only the event-level notes apply
      baseLeave,
      baseReturn: rawReturn < baseLeave ? baseLeave : rawReturn,
      isTour: !!g.tourId,
      tourName: g.tourId ? (tourNameById.get(g.tourId) ?? 'Tour') : null,
      firstEventId: first.id,
      firstEventDate: first.event_date,
      lastEventId: last.id,
      lastEventEnd: last.end_date || last.event_date,
      subLane: 0, // these bands pack into full-height lanes (rows), not sub-lanes
      conflict: false, // no vehicle, so never a same-vehicle conflict
    });
  }

  bands.sort((a, b) => (a.baseLeave < b.baseLeave ? -1 : a.baseLeave > b.baseLeave ? 1 : (a.key < b.key ? -1 : 1)));

  const lanes: DerivedVehicleBand[][] = [];
  const laneEnds: string[] = [];
  for (const band of bands) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      // Fits if it starts strictly after this lane's last band ends (same day = overlap).
      if (laneEnds[i] < band.baseLeave) {
        lanes[i].push(band);
        laneEnds[i] = band.baseReturn;
        placed = true;
        break;
      }
    }
    if (!placed) { lanes.push([band]); laneEnds.push(band.baseReturn); }
  }
  return lanes;
}

/** A maintenance/OOC range as one labeled block. */
export interface MaintRun {
  id: string;
  notes: string;
  start: string;
  end: string;
}
